import React from 'react'
import { Card, Form, Alert, Button, Col, Container, InputGroup, Row, Badge } from 'react-bootstrap';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L, { Icon } from 'leaflet';
import { MapPin } from 'react-feather';
import usePalette from '../../../hooks/usePalette';
import { Loader } from 'react-feather';
import ErrorAlert from '../../ErrorAlert';
import markerIcon from '../../../assets/img/brands/leaflet/marker-icon.png';
import markerIconShadow from '../../../assets/img/brands/leaflet/marker-shadow.png';
import { useGetCompaniesQuery } from '../../../redux/slices/companiesSlice';
import CustomSelect from '../../CustomSelect';
import { Formik, useFormik, FormikProvider } from 'formik';
import { useGetCapabilitiesQuery } from '../../../redux/slices/capabilitiesSlice';
import { useGetCategoryQuery } from '../../../redux/slices/anzsicSlice';
import { useGetIndustriesQuery } from '../../../redux/slices/industriesSlice';
import { useGetRegionsQuery } from '../../../redux/slices/regionsSlice';
import { Region } from '../../../types/region';

const CompanyMap = () => {
    const palette = usePalette();

    const { data: companies, isLoading: isLoadingCompanies, isSuccess: isSuccessCompanies, isError: isErrorCompanies, error: errorCompanies } = useGetCompaniesQuery(undefined,
        {
            refetchOnMountOrArgChange: true,
        }
    )

    const { data: capabilities, isLoading: isLoadingCapabilities, isSuccess: isSuccessCapabilities, isError: isErrorCapabilities, error: errorCapabilities } = useGetCapabilitiesQuery(undefined,
        {
            refetchOnMountOrArgChange: true,
        }
    )

    const { data: categories, isLoading: isLoadingCategories, isSuccess: isSuccessCategories, isError: isErrorCategories, error: errorCategories } = useGetCategoryQuery(undefined,
        {
            refetchOnMountOrArgChange: true,
        }
    )

    const { data: industries, isLoading: isLoadingIndustries, isSuccess: isSuccessIndustries, isError: isErrorIndustries, error: errorIndustries } = useGetIndustriesQuery(undefined,
        {
            refetchOnMountOrArgChange: true,
        }
    )

    const { data: regions, isLoading: isLoadingRegions, isSuccess: isSuccessRegions, isError: isErrorRegions, error: errorRegions } = useGetRegionsQuery(undefined, {
        refetchOnMountOrArgChange: true,
    })


    const formik = useFormik({
        initialValues: {
            categories: [],
            industries: [],
            subIndustries: [],
            capabilities: [],
            submit: false,
        },
        enableReinitialize: true,
        onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
            try {
                console.log('values', values)
            } catch (error: any) {
                console.log('error', error);
                const message = error.response ? error.response.data.message : error.error ? error.error : 'Something went wrong'

                setStatus({ success: false })
                setErrors({ submit: message })
                setSubmitting(false);
            }
        }
    })

    let content;

    if (isLoadingCompanies || isLoadingCapabilities || isLoadingCategories || isLoadingIndustries || isLoadingRegions) {
        content = (
            <Card>
                <Card.Header>
                    <Card.Title className="mb-0">Map Loading...</Card.Title>
                    <Card.Body>
                        <Loader />
                    </Card.Body>
                </Card.Header>
            </Card>
        )
    } else if (isErrorCompanies || isErrorCapabilities || isErrorCategories || isErrorIndustries || isErrorRegions) {
        content = (
            <Card>
                <ErrorAlert title="Error Loading Maps.." error={JSON.stringify(errorCompanies, null, 2)} />
            </Card>
        )
    } else if (isSuccessCompanies && companies && isSuccessCapabilities && capabilities && isSuccessCategories && categories && isSuccessIndustries && industries && isSuccessRegions && regions) {


        const icon = L.icon({
            iconUrl: markerIcon,
            iconSize: [18, 26],
            popupAnchor: [0, -15],
            shadowUrl: markerIconShadow,
            shadowAnchor: [13, 28]
        })

        let categoryOptions: any[] = [];

        categories.map((category) => {
            categoryOptions.push({
                label: category.name,
                value: category._id,
            })
        })

        let capabilityOptions: any[] = [];

        capabilities.map((capability) => {
            capabilityOptions.push({
                label: capability.name,
                value: capability._id,
            })
        })

        let industryOptions: any[] = [];

        industries.filter(industry => industry.sub === false).map((industry) => {
            industryOptions.push({
                label: industry.name,
                value: industry._id,
            })
        })

        let subIndustryOptions: any[] = [];

        industries.filter(industry => industry.sub === true).map((industry) => {
            subIndustryOptions.push({
                label: industry.name,
                value: industry._id,
            })
        })

        // console.log('regions', regions);

        // console.log('companies', companies);

        let companiesRegionsList: any[] = [];

        // need to get companies per region
        companies.forEach(company => {
            companiesRegionsList.push({
                companyId: company._id,
                companyName: company.name,
                companyPostcode: company.postcode,
                region: company.region,
            })
        })

        let merged: any[] = [];

        for (let i = 0; i < companiesRegionsList.length; i++) {
            merged.push({
                ...companiesRegionsList[i],
                //@ts-ignore
                ...(regions.find((itmInner) => itmInner.postcode === companiesRegionsList[i].companyPostcode))
            })
        }

        // console.log('merged', merged);

        let regionsWithCompanies = merged.reduce((acc, company) => {
            const region = acc.find(item => item.postcode === company.postcode);
            if (region) {
                region.companies.push(company)
            } else {
                acc.push({
                    region: company.region,
                    postcode: company.postcode,
                    lat: company.lat,
                    lon: company.lon,
                    companies: [company]
                })
            }
            return acc;
        }, [])

        // console.log('region', regionsWithCompanies);

        let regionsFiltered = regionsWithCompanies.filter((region) => region.region !== 'Other');

        // console.log('regionsFiltered', regionsFiltered);


        content = (
            <>
                <Card>
                    <Card.Header>
                        <Card.Title>Map Detail Selector <Badge color="blue">Coming Soon</Badge></Card.Title>
                    </Card.Header>
                    <Card.Body className="text-center">
                        <Form onSubmit={formik.handleSubmit}>
                            {formik.errors.submit && (
                                <Alert className="my-3" variant="danger">
                                    <div className="alert-message">{formik.errors.submit}</div>
                                </Alert>
                            )}
                            <Row>
                                <Form.Group className="mb-3" as={Col} md={3}>
                                    <Form.Label><strong>Category</strong></Form.Label>
                                    <CustomSelect
                                        className="react-select-container"
                                        //@ts-ignore
                                        onChange={value => value.forEach((val, index) => formik.setFieldValue(`categories[${index}]`, val.value))}
                                        //@ts-ignore
                                        value={formik.values.categories[0]?._id}
                                        options={categoryOptions}
                                        multiSelect={true}
                                    // isSearchable={false}
                                    />
                                    {!!formik.touched?.categories && (
                                        <div style={{
                                            display: 'block',
                                            width: '100%',
                                            marginTop: '0.25rem',
                                            fontSize: '80%',
                                            color: '#d9534f'
                                        }}>
                                            {formik.errors?.categories}
                                        </div>
                                    )}
                                </Form.Group>
                                <Form.Group className="mb-3" as={Col} md={3}>
                                    <Form.Label><strong>Industry</strong></Form.Label>
                                    <CustomSelect
                                        className="react-select-container"
                                        //@ts-ignore
                                        onChange={value => value.forEach((val, index) => formik.setFieldValue(`industries[${index}]`, val.value))}
                                        //@ts-ignore
                                        value={formik.values.industries[0]?._id}
                                        options={industryOptions}
                                        multiSelect={true}
                                    />
                                    {!!formik.touched?.industries && (
                                        <div style={{
                                            display: 'block',
                                            width: '100%',
                                            marginTop: '0.25rem',
                                            fontSize: '80%',
                                            color: '#d9534f'
                                        }}>
                                            {formik.errors?.industries}
                                        </div>
                                    )}
                                </Form.Group>
                                <Form.Group className="mb-3" as={Col} md={3}>
                                    <Form.Label><strong>Sub-Industry</strong></Form.Label>
                                    <CustomSelect
                                        className="react-select-container"
                                        //@ts-ignore
                                        onChange={value => value.forEach((val, index) => formik.setFieldValue(`subIndustries[${index}]`, val.value))}
                                        //@ts-ignore
                                        value={formik.values.subIndustries[0]?._id}
                                        options={subIndustryOptions}
                                        multiSelect={true}
                                    />
                                    {!!formik.touched?.subIndustries && (
                                        <div style={{
                                            display: 'block',
                                            width: '100%',
                                            marginTop: '0.25rem',
                                            fontSize: '80%',
                                            color: '#d9534f'
                                        }}>
                                            {formik.errors?.subIndustries}
                                        </div>
                                    )}
                                </Form.Group>
                                <Form.Group className="mb-3" as={Col} md={3}>
                                    <Form.Label><strong>Capability</strong></Form.Label>
                                    <CustomSelect
                                        className="react-select-container"
                                        //@ts-ignore
                                        onChange={value => value.forEach((val, index) => formik.setFieldValue(`capabilities[${index}]`, val.value))}
                                        //@ts-ignore
                                        value={formik.values.capabilities[0]?._id}
                                        options={capabilityOptions}
                                        multiSelect={true}
                                    />
                                    {!!formik.touched?.capabilities && (
                                        <div style={{
                                            display: 'block',
                                            width: '100%',
                                            marginTop: '0.25rem',
                                            fontSize: '80%',
                                            color: '#d9534f'
                                        }}>
                                            {formik.errors?.capabilities}
                                        </div>
                                    )}
                                </Form.Group>
                                <Form.Group className="mb-3" as={Col} md={12}>
                                    <Button
                                        className="mt-3 text-center"
                                        type="submit"
                                        variant="primary"
                                        size="lg"
                                        disabled={formik.isSubmitting}
                                    >
                                        Select
                                    </Button>
                                </Form.Group>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Header>
                        <Card.Title className="mb-0">Map</Card.Title>
                    </Card.Header>
                    <Card.Body className="text-center">

                        <MapContainer
                            style={{ height: '80vh', zIndex: 0 }}
                            center={[-26.6061069, 152.9340734]} // Highlights on Sunshine Coast centrally.

                            // -25.6091623 134.3618174 - Center of Australia
                            zoom={10}
                            scrollWheelZoom={false}
                        >
                            {/* {companies && companies.map((company, index) => (
                            <Marker
                                key={index}
                                icon={icon}
                                position={[company.lat, company.lon]}
                            >
                                <Popup
                                    closeOnClick
                                    closeOnEscapeKey
                                    autoClose
                                    closeButton={false}
                                >
                                    <ul>
                                        {company?.companies.map(comp => (
                                            <li>{comp.name}</li>
                                        ))}
                                    </ul>
                                </Popup>
                            </Marker>
                        ))} */}
                            {regionsFiltered && regionsFiltered.map((region: any, index) => (
                                <Marker
                                    key={index}
                                    icon={icon}
                                    position={[region?.lat ? region?.lat : -26.6061069, region?.lon ? region?.lon : 152.9340734]}
                                >
                                    <Popup
                                        closeOnClick
                                        closeOnEscapeKey
                                        autoClose
                                        closeButton={false}
                                    >
                                        <ul>
                                            {region?.companies.map((comp, i) => (
                                                <li key={i}>{comp?.companyName}</li>
                                            ))}
                                        </ul>
                                    </Popup>
                                </Marker>
                            ))}
                            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        </MapContainer>

                    </Card.Body>
                </Card>
            </>

        )
    } else {
        content = (
            <Card>
                <Card.Header>
                    <Card.Title className="mb-0">Map Loading...</Card.Title>
                    <Card.Body>
                        <Loader />
                    </Card.Body>
                </Card.Header>
            </Card>
        )
    }

    return content;
}

export default CompanyMap